import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { clone } from '@jack-henry/frontend-utils/functions';
import { DynamicComponentExitReason } from '@jack-henry/frontend-utils/lit/dynamic-component-registry';
import { ConfigurationService, Environment } from '@treasury/core/config';
import { NavigationService } from '@treasury/core/navigation';
import { UserActivityService } from '@treasury/domain/channel/services';
import { DashboardService, InformationCenterMessage } from '@treasury/domain/dashboard';
import { DashboardWidget } from '@treasury/domain/dashboard/dashboard-widget.entity';
import { NavigationsService } from '@treasury/domain/navigation';
import { Feature } from '@treasury/domain/services/feature-flags';
import {
    FeatureVisibilityService,
    PwaVisibilityFeature,
} from '@treasury/domain/services/feature-visibility';
import { TmContainer } from '@treasury/presentation';
import {
    achIcon,
    billIcon,
    billsIcon,
    depositIcon,
    informationIcon,
    transferIcon,
    wiresIcon,
} from '@treasury/presentation/assets/icons';
import '@treasury/presentation/components/tm-accordion';
import '@treasury/presentation/components/tm-action-tile';
import '@treasury/presentation/components/tm-icon';
import { TmDialogService } from '@treasury/presentation/services/dialog';
import { SessionStorageService } from '@treasury/utils';
import { css, html, nothing, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { HeaderBarService } from '../../services/jhd-header-bar.service';
import { MenuItem } from '../../services/types/jhd-header-bar.type';
import '../components/accounts-widget';
import '../components/approval-widget';
import '../components/check-lookup-widget';
import '../components/configure-widgets/configure-widgets-button';
import '../components/configure-widgets/configure-widgets-sheet';
import '../components/cutoff-times-header-item';
import '../components/feature-search';
import '../components/information-center-banner';
import '../components/information-center-widget';
import '../components/loan-payment-widget';
import '../components/messages-header-item';
import '../components/notifications-header-item';
import '../components/positive-pay-decision-widget';
import '../components/profile-header-item';
import '../components/reports-widget';
import '../components/resource-center-widget';
import '../components/total-cash-position-graph';
import '../components/transfer-payment-widget';
import { InformationCenterItemVm } from '../data/information-center-item.vm';

const INFO_CENTER_MODAL_KEY = 'informationCenterModal';

export const tagName = 'jhd-dashboard';
@customElement(tagName)
export class JhdDashboard extends TmContainer {
    @InjectProperty()
    declare private headerService: HeaderBarService;

    @InjectProperty()
    declare private navService: NavigationService;

    @InjectProperty()
    declare private readonly navigationsService: NavigationsService;

    @InjectProperty()
    declare private config: ConfigurationService;

    @InjectProperty()
    declare private dashboardService: DashboardService;

    @InjectProperty()
    declare private sessionStorageService: SessionStorageService;

    @InjectProperty()
    declare private readonly dialogService: TmDialogService;

    @InjectProperty({
        async: true,
        token: FeatureVisibilityService,
    })
    declare private featureVisibilityServicePromise: Promise<FeatureVisibilityService>;

    @state()
    private featureVisibilityService?: FeatureVisibilityService;

    @state()
    private showCutoffTooltip = false;

    @state()
    private showNotificationsTooltip = false;

    @property({ type: Array })
    private menuItems = this.createMenuItems();

    @state()
    private bottomSheetOpen = false;

    @state()
    private configuredProducts: Map<string, boolean> = new Map();

    @state()
    private configuredWidgets: DashboardWidget[] = [];

    @state()
    private bannerItem?: InformationCenterItemVm;

    @state()
    private banners: InformationCenterItemVm[] = [];

    @state()
    private hasConfigureDashboard = false;

    @state()
    private configureDashboardOpen = false;

    @state()
    private hasRouteToLegacyChannel = false;

    private widgetList = [
        {
            id: 'accounts',
            type: 'accounts',
            column: 1,
            renderer: () => this.renderAccountsWidget(),
        },
        { type: 'infoCenter', column: 2, renderer: () => this.renderInformationCenterWidget() },
        { type: 'resources', column: 2, renderer: () => this.renderResourceCenterWidget() },
        {
            id: 'reports',
            type: 'favoriteReports',
            column: 2,
            renderer: () => this.renderReportsWidget(),
        },
        { type: 'arp', column: 2, renderer: () => this.renderPositivePayWidget() },
        { type: 'quickTransfer', column: 2, renderer: () => this.renderQuickTransferWidget() },
        { type: 'stopRequest', column: 2, renderer: () => nothing },
        {
            id: 'pending-approval',
            type: 'paymentsPendingApproval',
            column: 2,
            renderer: () => this.renderApprovalWidget(),
        },
        { type: 'quickLoanPayment', column: 2, renderer: () => this.renderLoanWidget() },
    ];

    private availableWidgetsList = clone(this.widgetList);

    public async connectedCallback() {
        super.connectedCallback();
        this.loading = true;

        this.showNotificationsTooltip = screen.width > 1100;

        this.featureVisibilityService = await this.featureVisibilityServicePromise;

        this.hasRouteToLegacyChannel =
            (await (await this.featureFlagService).isEnabled(Feature.PwaRouteToLegacyChannel)) &&
            this.deviceService.isDesktop;

        this.hasConfigureDashboard = await (
            await this.featureFlagService
        ).isEnabled(Feature.PwaConfigureDashboard);

        await this.loadWidgets();
        this.scrollToLastWidgetSelected();

        this.loading = false;

        await this.handleInformationCenterMessages();
    }

    public disconnectedCallback(): void {
        super.disconnectedCallback();
        this.dialogService.close();
    }

    protected firstUpdated() {
        this.headerService.configure({
            title: 'Dashboard',
            menuItems: this.menuItems,
        });
    }

    protected render() {
        return html`
            <div class="header-pad flex flex-1 -mb-[124px] pb-24 bg-header shadow">&nbsp;</div>
            <slot></slot>
            <div class="action-tiles flex mx-1 mt-1 mb-2">
                ${this.renderActionTile(
                    'ACH Payments',
                    achIcon,
                    '/ach',
                    false,
                    this.isFeatureVisible(PwaVisibilityFeature.ACH)
                )}
                ${this.renderActionTile(
                    'Business Bill Pay',
                    billsIcon,
                    '/business-bill-pay',
                    false,
                    this.isFeatureVisible(PwaVisibilityFeature.BillPay)
                )}
                ${this.renderActionTile(
                    'Deposits',
                    depositIcon,
                    '/deposit',
                    false,
                    this.isFeatureVisible(PwaVisibilityFeature.RDC)
                )}
                ${this.renderActionTile(
                    'Transfers',
                    transferIcon,
                    '/transfers',
                    false,
                    this.isFeatureVisible(PwaVisibilityFeature.Transfers)
                )}
                ${this.renderActionTile(
                    'Wires',
                    wiresIcon,
                    '/wires',
                    false,
                    this.isFeatureVisible(PwaVisibilityFeature.Wires)
                )}
                ${this.renderActionTile(
                    'Loans',
                    billIcon,
                    '/loans',
                    false,
                    this.isFeatureVisible(PwaVisibilityFeature.Loans)
                )}
            </div>
            ${this.banners.map(
                b => html`<information-center-banner .message=${b}></information-center-banner>`
            )}
            ${this.hasRouteToLegacyChannel ? this.renderRouteToLegacyChannelBanner() : nothing}
            ${this.loading || !this.isFeatureVisible(PwaVisibilityFeature.DashboardFeatureSearch)
                ? nothing
                : html`<div
                      class=${classMap({
                          'mt-8': !this.deviceService.isDesktop,
                      })}
                  >
                      <feature-search></feature-search>
                  </div>`}
            ${this.loading
                ? this.renderBlockingLoader()
                : html`<div class="widget-wrapper flex flex-wrap items-start mx-2">
                      <div class="main-widgets-wrapper">
                          ${this.hasConfigureDashboard
                              ? this.widgetList
                                    .filter(widget => {
                                        if (this.deviceService.isDesktop) {
                                            return widget.column === 1;
                                        }
                                        return false;
                                    })
                                    .map(w => w.renderer())
                              : html`${this.renderAccountsWidget()}`}
                      </div>
                      <div class="shortcut-widgets-wrapper">
                          ${this.hasConfigureDashboard
                              ? this.widgetList
                                    .filter(widget => {
                                        if (this.deviceService.isDesktop) {
                                            return widget.column === 2;
                                        }
                                        return true;
                                    })
                                    .map(w => w.renderer())
                              : html`<approval-widget
                                        .products=${this.configuredProducts}
                                    ></approval-widget
                                    >${this.renderPositivePayWidget()}
                                    ${this.renderInformationCenterWidget()}
                                    ${this.renderResourceCenterWidget()}
                                    ${this.renderReportsWidget()}${this.renderLoanWidget()}`}${this.renderCheckLookupWidget()}
                          <configure-widgets-button
                              @configure-dashboard=${() => {
                                  this.configureDashboardOpen = true;
                              }}
                          ></configure-widgets-button>
                      </div>
                  </div>`}
            <configure-widgets-sheet
                .open=${this.configureDashboardOpen}
                @dashboard-configured=${() => {
                    this.loadWidgets();
                }}
                @close=${() => {
                    this.configureDashboardOpen = false;
                }}
            ></configure-widgets-sheet>
        `;
    }

    private isFeatureVisible(feature: PwaVisibilityFeature): boolean {
        return this.featureVisibilityService
            ? this.featureVisibilityService.isFeatureVisible(feature)
            : false;
    }

    private scrollToWidget(id: string | undefined) {
        if (!id) {
            return;
        }
        const widget = this.shadowRoot?.getElementById(id);
        if (widget) {
            widget.scrollIntoView(true);
        }
    }

    private scrollToLastWidgetSelected() {
        const previousRoute = this.navService.previousRoute?.route;
        for (const widget of this.widgetList) {
            if (widget.id && previousRoute?.includes(widget.id)) {
                setTimeout(() => {
                    this.scrollToWidget(widget.id);
                }, 200);
                break;
            }
        }
    }

    private async handleInformationCenterMessages() {
        if (!this.isFeatureVisible(PwaVisibilityFeature.InfoCenterWidget)) {
            return;
        }

        const { modal, banners } =
            await this.dashboardService.getInformationCenterBannerModalMessages();

        if (banners.length > 0) {
            this.banners = banners.map(b => new InformationCenterItemVm(b));
        }

        const { route } = await this.navService.getRouteData();

        if (modal && route.includes('dashboard')) {
            this.displayInformationCenterDialog(new InformationCenterItemVm(modal));
        }
    }

    private createMenuItems(): MenuItem[] {
        return [
            {
                title: 'Messages',
                icon: html`<messages-header-item></messages-header-item>`,
                action: () => this.navService.navigate(`/message-center`),
            },
            {
                title: 'Notifications',
                icon: html`<notifications-header-item
                    .institution=${this.config.institutionId}
                    .showNotificationsTooltip=${this.deviceService.isDesktop}
                ></notifications-header-item>`,
                action: () => {
                    if (!this.showNotificationsTooltip) {
                        this.navService.navigate(`/notifications`);
                    }
                },
            },
            {
                title: 'Cutoff Times',
                icon: html`<cutoff-times-header-item
                    .showCutoffTooltip=${this.showCutoffTooltip}
                ></cutoff-times-header-item>`,
                action: () => {
                    this.showCutoffTooltip = true;
                },
            },
            {
                title: 'Profile',
                icon: html`<profile-header-item></profile-header-item>`,
                action: () => this.navService.navigate(`/profile`),
            },
        ];
    }

    private async displayInformationCenterDialog(modalItem: InformationCenterItemVm) {
        if (this.hasSeenModal(modalItem) || this.dialogService.isDialogOpen) {
            return;
        }

        const { message } = modalItem;
        this.setModalAsSeen(modalItem);
        // Note, the refactor to the dialog service removed the Vm styling we previously had.
        const result = await this.dialogService.open(message.description, message.title, {
            buttons: [
                {
                    label: 'Ok',
                    reason: DynamicComponentExitReason.Confirm,
                },
            ],
            classes: ['max-w-[700px]'],
            headerIcon: informationIcon,
        });

        if (result.reason === DynamicComponentExitReason.Confirm) {
            UserActivityService.saveUserActivity(4, 'Acknowledged a modal message');
        }
    }

    private hasSeenModal(modalItem: InformationCenterItemVm) {
        // logic basically makes the assumption that if the key has been stored, the user
        // has logged in and seen the modal
        const storedValue = this.sessionStorageService.get(INFO_CENTER_MODAL_KEY);
        if (!storedValue) {
            return false;
        }
        return modalItem.message.entityId !== storedValue;
    }

    private setModalAsSeen(modalItem: InformationCenterItemVm) {
        if (this.sessionStorageService.get(INFO_CENTER_MODAL_KEY)) {
            return;
        }

        this.sessionStorageService.set(INFO_CENTER_MODAL_KEY, modalItem.message.entityId);
    }

    private async loadWidgets() {
        if (this.deviceService.isDesktop) {
            await this.loadDashboardWidgets();
        } else {
            await this.loadMobileWidgets();
        }
    }

    private async loadMobileWidgets() {
        try {
            this.configuredWidgets = await this.dashboardService.getConfiguredMobileWidgets();
            this.widgetList = this.availableWidgetsList;
            this.sortAndFilterWidgets();
        } catch {
            this.notificationService.renderError('Failed to load dashboard widgets');
        }
    }

    private async loadDashboardWidgets() {
        try {
            this.configuredWidgets = await this.dashboardService.getConfiguredWidgets();
            this.widgetList = this.availableWidgetsList;
            this.sortAndFilterWidgets();
            // for larger viewports, set the column based on the configuredWidgets left property
            this.widgetList = this.widgetList.map(w => {
                const configuredWidget = this.configuredWidgets.find(
                    widget => widget.type === w.type
                );
                return { ...w, column: configuredWidget?.left === 0 ? 1 : 2 };
            });
        } catch {
            this.notificationService.renderError('Failed to load dashboard widgets');
        }
    }

    private sortAndFilterWidgets() {
        this.widgetList = this.widgetList.sort((a, b) => {
            const aIndex = this.configuredWidgets.findIndex(w => w.type === a.type);
            const bIndex = this.configuredWidgets.findIndex(w => w.type === b.type);
            return aIndex - bIndex;
        });
        this.widgetList = this.widgetList.filter(w =>
            this.configuredWidgets.some(widget => widget.type === w.type)
        );
    }

    private renderActionTile(
        tileTitle: string,
        icon: string | TemplateResult,
        actionPath: string,
        disabled: boolean,
        visible: boolean | undefined
    ) {
        if (!visible) {
            return nothing;
        }
        return html`
            <tm-action-tile
                .tileTitle=${tileTitle}
                .icon=${icon}
                .action=${() => {
                    this.navService.navigate(actionPath);
                }}
                .disabled=${disabled}
            ></tm-action-tile>
        `;
    }

    private renderRouteToLegacyChannelBanner() {
        const message = new InformationCenterItemVm(
            new InformationCenterMessage({
                title: 'Desktop Experience',
                description: html`Looking for the fully featured Treasury desktop experience?
                    <tm-button
                        importance="tertiary"
                        size="small"
                        class="ml-2"
                        @click=${() => {
                            const isLocal = this.config.environment === Environment.Local;
                            const { institutionId } = this.config;
                            if (isLocal) {
                                window.location.href = `http://localhost:9002/${institutionId}/dashboard`;
                            } else {
                                window.location.href = `${window.location.origin}/${institutionId}/dashboard`;
                            }
                        }}
                        >Click here.</tm-button
                    >` as unknown as string,
                type: 'Banner',
                sequence: 1,
            })
        );
        return html` <information-center-banner .message=${message}></information-center-banner>`;
    }

    private renderAccountsWidget() {
        if (!this.isFeatureVisible(PwaVisibilityFeature.AccountsWidget)) {
            return nothing;
        }
        return html`<accounts-widget></accounts-widget>`;
    }

    private renderApprovalWidget() {
        if (!this.isFeatureVisible(PwaVisibilityFeature.PaymentApprovalWidget)) {
            return nothing;
        }
        return html`<approval-widget .products=${this.configuredProducts}></approval-widget>`;
    }

    private renderPositivePayWidget() {
        if (!this.isFeatureVisible(PwaVisibilityFeature.PosPayWidget)) {
            return nothing;
        }
        return html` <positive-pay-decision-widget></positive-pay-decision-widget> `;
    }

    private renderInformationCenterWidget() {
        if (!this.isFeatureVisible(PwaVisibilityFeature.InfoCenterWidget)) {
            return nothing;
        }
        return html`<information-center-widget></information-center-widget>`;
    }

    private renderResourceCenterWidget() {
        if (!this.isFeatureVisible(PwaVisibilityFeature.ResourcesWidget)) {
            return nothing;
        }
        return html`<resource-center-widget></resource-center-widget>`;
    }

    private renderReportsWidget() {
        if (!this.isFeatureVisible(PwaVisibilityFeature.ReportsWidget)) {
            return nothing;
        }
        return html`<reports-widget id="reports"></reports-widget>`;
    }

    private renderLoanWidget() {
        if (!this.isFeatureVisible(PwaVisibilityFeature.LoanWidget)) {
            return nothing;
        }
        return html`<loan-payment-widget></loan-payment-widget>`;
    }

    private renderQuickTransferWidget() {
        if (!this.isFeatureVisible(PwaVisibilityFeature.TransferWidget)) {
            return nothing;
        }
        return html`<transfer-payment-widget></transfer-payment-widget>`;
    }

    private renderCheckLookupWidget() {
        if (!this.isFeatureVisible(PwaVisibilityFeature.CheckLookupWidget)) {
            return nothing;
        }
        return html`<check-lookup-widget></check-lookup-widget>`;
    }

    private renderBlockingLoader() {
        if (!this.loading) {
            return nothing;
        }
        return html`<tm-blocking-loader></tm-blocking-loader>`;
    }

    public static get styles() {
        return [
            css`
                :host {
                    --widget-gutter: 10px;
                }
                .main-widgets-wrapper {
                    flex: 1;
                }

                .main-widgets-wrapper > *,
                .shortcut-widgets-wrapper > * {
                    margin-bottom: var(--widget-gutter);
                }
                .shortcut-widgets-wrapper {
                    margin: 0;
                    width: 100%;
                }
                .action-tiles {
                    overflow-x: scroll;
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }

                @media screen and (min-width: 900px) {
                    .shortcut-widgets-wrapper {
                        margin-right: 0;
                        margin-left: var(--widget-gutter);
                        width: unset;
                        flex: 2 4 auto;
                    }
                    .main-widgets-wrapper {
                        flex: 5 1 0;
                    }
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: JhdDashboard;
    }
}
